import type { ArtistMessage, FanToArtistMessage, ModerationItem, RetractedArtistMessage } from "@kira/contracts";
import { realtimeEvents } from "@kira/contracts";
import { io, type Socket } from "socket.io-client";

type MessageHandler = (message: ArtistMessage) => void;
type MessageRetractedHandler = (message: RetractedArtistMessage) => void;
type StatusHandler = (connected: boolean) => void;
type FanMessageHandler = (message: FanToArtistMessage) => void;
type ModerationHandler = (item: ModerationItem) => void;

export class KiraRealtimeClient {
  private socket: Socket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private ready = false;
  private readonly orderHandlers = new Set<() => void>();
  private readonly subscriptionHandlers = new Set<(change: { artist: { id: string }; status: string | null }) => void>();
  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly messageRetractedHandlers = new Set<MessageRetractedHandler>();
  private readonly statusHandlers = new Set<StatusHandler>();
  private readonly fanMessageHandlers = new Set<FanMessageHandler>();
  private readonly moderationHandlers = new Set<ModerationHandler>();

  connect(): void {
    this.stopped = false;
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }
    const socket = io({
      path: "/socket.io",
      withCredentials: true,
      // Establish the reliable HTTP transport first, then upgrade to WebSocket.
      // Local proxies and temporary tunnels can reject an individual WebSocket
      // handshake; polling keeps realtime delivery alive while they recover.
      transports: ["polling", "websocket"],
      tryAllTransports: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 600,
      reconnectionDelayMax: 8_000,
      timeout: 8_000,
    });
    this.socket = socket;
    socket.on("session:ready", () => {
      this.clearReconnectTimer();
      this.setReady(true);
    });
    socket.on("connect_error", () => {
      this.setReady(false);
      this.queueReconnect();
    });
    socket.on("disconnect", () => {
      this.setReady(false);
      this.queueReconnect();
    });
    socket.on(realtimeEvents.ordersUpdated, () => { for (const handler of this.orderHandlers) handler(); });
    socket.on(realtimeEvents.subscriptionUpdated, change => { for (const handler of this.subscriptionHandlers) handler(change); });
    socket.on(realtimeEvents.artistMessage, (message: ArtistMessage) => {
      for (const handler of this.messageHandlers) handler(message);
    });
    socket.on(realtimeEvents.messageRetracted, (message: RetractedArtistMessage) => {
      for (const handler of this.messageRetractedHandlers) handler(message);
    });
    socket.on(realtimeEvents.artistFanMessage, (message: FanToArtistMessage) => {
      for (const handler of this.fanMessageHandlers) handler(message);
    });
    socket.on(realtimeEvents.moderationUpdated, (item: ModerationItem) => {
      for (const handler of this.moderationHandlers) handler(item);
    });
    window.addEventListener("online", this.retryConnection);
    document.addEventListener("visibilitychange", this.retryWhenVisible);
  }

  onOrdersUpdated(handler: () => void): () => void {
    this.orderHandlers.add(handler);
    return () => this.orderHandlers.delete(handler);
  }

  onSubscriptionUpdated(handler: (change: { artist: { id: string }; status: string | null }) => void): () => void {
    this.subscriptionHandlers.add(handler);
    return () => this.subscriptionHandlers.delete(handler);
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onMessageRetracted(handler: MessageRetractedHandler): () => void {
    this.messageRetractedHandlers.add(handler);
    return () => this.messageRetractedHandlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.ready);
    return () => this.statusHandlers.delete(handler);
  }

  onFanMessage(handler: FanMessageHandler): () => void {
    this.fanMessageHandlers.add(handler);
    return () => this.fanMessageHandlers.delete(handler);
  }

  onModerationUpdated(handler: ModerationHandler): () => void {
    this.moderationHandlers.add(handler);
    return () => this.moderationHandlers.delete(handler);
  }

  disconnect(): void {
    this.stopped = true;
    this.clearReconnectTimer();
    window.removeEventListener("online", this.retryConnection);
    document.removeEventListener("visibilitychange", this.retryWhenVisible);
    this.socket?.disconnect();
    this.socket = null;
    this.setReady(false);
  }

  private readonly retryConnection = () => {
    if (this.stopped || this.ready || !this.socket) return;
    this.clearReconnectTimer();
    this.socket.connect();
  };

  private readonly retryWhenVisible = () => {
    if (document.visibilityState === "visible") this.retryConnection();
  };

  private queueReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.retryConnection();
    }, 1_000);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setReady(ready: boolean): void {
    if (this.ready === ready) return;
    this.ready = ready;
    for (const handler of this.statusHandlers) handler(ready);
  }
}
